// Recovery of a chat turn that outlived its stream. Pure — no React, no DOM —
// so the dashboard's vitest runner can pin every rule (same layout as
// turnRetry.ts).
//
// WHY this exists. Turns are durable server-side: the graph run is detached
// from the HTTP connection (app/graphs/service.py::_drive_turn), so a client
// that navigates away mid-answer only stops WATCHING — the answer still lands
// in the checkpointer. This is the client's half of that contract: a remount
// that finds a `pendingTurn` marker (or a History open of a `busy` thread)
// polls the thread replay and decides, per poll, whether to keep waiting,
// adopt what the server has, or declare the turn dead and offer a Retry.
// Before this pair of changes the turn was simply thrown away on both sides —
// the beta ticket: "move away before the AI responds and that chat never
// finishes to process."

import type { LoadedThread } from './types'

/** What kind of send the orphaned turn was — a fresh user message, or the
 *  answer to a clarify question (`resume_value`). They replay differently: a
 *  resume's answer is consumed by the interrupt and never checkpointed as a
 *  user message, so `userText` containment is only meaningful for messages. */
export type PendingTurnKind = 'message' | 'resume'

export interface PendingTurnInfo {
  kind: PendingTurnKind
  /** The trailing user bubble of the local transcript (null when unknown). */
  userText: string | null
}

/** Poll cadence while the server reports the turn in flight. */
export const RECOVER_POLL_MS = 2_500

/** Consecutive failed replay fetches before recovery gives up — the API being
 *  unreachable this long means there is nothing to re-attach to for now; the
 *  failed card's Retry re-sends when it comes back. */
export const RECOVER_MAX_FETCH_FAILURES = 8

/** Absolute ceiling on one recovery, comfortably above the server's own
 *  per-turn budget (service.TURN_BUDGET_SECONDS, 15 min): a thread still
 *  reported busy past this is treated as wedged. */
export const RECOVER_DEADLINE_MS = 20 * 60_000

/** Failure detail for a recovered turn whose run died (crash, explicit
 *  cancel, or a deploy restart mid-answer). */
export const RECOVERED_FAILURE_DETAIL =
  'This answer was interrupted before it finished. Retry to ask again.'

/** Failure detail when the turn died before the server even named a thread —
 *  a sub-second window after send; there is nothing to re-attach to. */
export const RECOVERED_NO_THREAD_DETAIL =
  'This message was interrupted before the answer started. Retry to send it again.'

export type RecoveryAction =
  /** A run is in flight on the thread — keep waiting. */
  | { kind: 'poll' }
  /** The thread has settled: replace the transcript with the replay (plus the
   *  pending clarify question, when one is waiting for an answer). */
  | { kind: 'adopt' }
  /** The run is over but the answer never landed. `mergeUserText` — the
   *  replay doesn't even contain the user's message (the run died before its
   *  first checkpoint): re-append the local bubble so the failed card's Retry
   *  has its question above it. */
  | { kind: 'failed'; mergeUserText: boolean }

const norm = (s: string) => s.trim()

function containsUserText(detail: LoadedThread, userText: string | null): boolean {
  if (!userText) return true // nothing to check against — trust the replay
  const want = norm(userText)
  return detail.messages.some((m) => m.role === 'user' && norm(m.content) === want)
}

/** One poll's verdict on a thread replay. */
export function planRecovery(detail: LoadedThread, pending: PendingTurnInfo): RecoveryAction {
  if (detail.busy) return { kind: 'poll' }
  if (detail.question) return { kind: 'adopt' } // paused on a clarify — re-ask it
  const last = detail.messages[detail.messages.length - 1]
  if (last?.role === 'assistant') {
    if (pending.kind === 'message' && !containsUserText(detail, pending.userText)) {
      // Answered, but not OUR question: the turn died before its message was
      // checkpointed, and the trailing answer belongs to an earlier turn.
      return { kind: 'failed', mergeUserText: true }
    }
    return { kind: 'adopt' }
  }
  // Idle and unanswered: the run died.
  return { kind: 'failed', mergeUserText: !containsUserText(detail, pending.userText) }
}
